import { CommonModule, CurrencyPipe } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, effect, inject, OnInit, ViewChild } from '@angular/core';
import { ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDialogModule } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import {
  buildSubscriptionSeatsCancelRequest,
  isSameFormulaEvent,
  RESERVED_SEAT_SCOPE,
  OrderDisplayType,
  OrdersActionLevel,
  OrdersActionPayload,
  OrdersActionType,
  SINGLE_MODE_INSTRUCTION_KIND,
  SPLIT_INSTRUCTION_KIND,
  SETTLED_SEAT_SCOPE,
  SUBSCRIPTION_CANCEL_ACTION,
  SUBSCRIPTION_REFUND_ACTION,
} from '@app/orders/models/orders.model';
import { OrdersCancelService } from '@app/orders/services/orders-cancel.service';
import { OrdersConfirmDialogService } from '@app/orders/services/orders-confirm-dialog.service';
import { OrdersOperationsService } from '@app/orders/services/orders-operations.service';
import { OrdersUtilsService } from '@app/orders/services/orders-utils.service';
import { OrdersService } from '@app/orders/services/orders.service';
import { PaymentTransactionService } from '@app/orders/services/payment-transaction.service';
import { Constants } from '@core/app-constants';
import { AlertService } from '@core/services/alert.service';
import {
  CancelOrderItemsRequest,
  CancelReservedOrderDetailsDto,
  CancelReservedOrderDto,
  CancelReservedOrderItemsAndSubscriptionsRequestDto,
  FinancialTransactionRequestDto,
  GetOrderItemDetailsResponseDto,
  OrderArticleProductResponseDto,
  OrderArticleSeatResponseDto,
  OrderFinancialTransactionRequestDto,
  OrderItemPaymentItemResponseDto,
  OrderItemPaymentResponseDto,
  OrderItemRowSubscriptionResponseDto,
  OrderItemStatusEnumResponseDto,
  OrderItemTypeEnumResponseDto,
  OrderRefundDetailsDto,
  OrderRefundDto,
  OrderSeatsCancelRequestDto,
  OrderSeatStatusEnumResponseDto,
  OrdersRefundRequestDto,
  OrderStatusEnumResponseDto,
  PaymentInstructionRequestDto,
  PaymentModeResponseDto,
  PaymentOperationTypeEnumRequestDto,
  PaymentOperationTypeEnumResponseDto,
  PaymentTypeEnumRequestDto,
  SeatStatusScopeRequestDto,
  SingleModePaymentRequestDto,
  SplitPaymentRequestDto,
} from '@core/services/apiclient';
import { ConfirmDialogService } from '@core/services/confirm-dialog.service';
import { NgbTooltipModule } from '@ng-bootstrap/ng-bootstrap';
import { select, Store } from '@ngrx/store';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import * as fromOrders from '@root-store/orders-store/index';
import { SalesService } from '@sales/services/sales.service';
import { DialogComponent } from '@shared/components';
import {
  AbstractPaymentModeFormComponent,
  AbstractPaymentModeFormUtils,
} from '@shared/components/functional/abstract-payment-mode-form/abstract-payment-mode-form.component';
import { CustomerDisplayItem } from '@shared/customer/models/customer-display-item.model';
import { CustomerSearchItem, CustomerType } from '@shared/customer/models/customer-search-item.model';
import { CustomerSearchStateService } from '@shared/customer/services/customer-search-state.service';
import { CustomerService } from '@shared/customer/services/customer.service';
import { PaymentModeModel } from '@shared/models/payment-mode.model';
import { SalesPaymentModeModel } from '@shared/models/sales-payment-mode.model';
import { MomentPipe } from '@shared/pipes/moment.date.format';
import { SharedModule } from '@shared/shared.module';
import { catchError, combineLatest, EMPTY, filter, of, take, takeUntil } from 'rxjs';
import { clone } from 'underscore';

@Component({
  selector: 'app-orders-folder-cancel-dialog',
  templateUrl: './orders-folder-cancel-dialog.component.html',
  styleUrls: ['./orders-folder-cancel-dialog.component.scss'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    TranslateModule,
    MatButtonModule,
    MatDialogModule,
    MatIconModule,
    NgbTooltipModule,
    SharedModule,
  ],
  providers: [CustomerSearchStateService],
})
export class OrdersFolderCancelDialogComponent extends AbstractPaymentModeFormComponent implements OnInit {
  @ViewChild(DialogComponent, { static: true }) dialog: DialogComponent;
  private readonly store = inject(Store<fromOrders.State>);
  private readonly customerService = inject(CustomerService);
  private readonly salesService = inject(SalesService);
  private readonly datePipe = inject(MomentPipe);
  private readonly currencyPipe = inject(CurrencyPipe);
  private readonly ordersCancelService = inject(OrdersCancelService);
  private readonly ordersUtilsService = inject(OrdersUtilsService);
  private readonly ordersService = inject(OrdersService);
  private readonly paymentTransactionService = inject(PaymentTransactionService);
  private readonly confirmDialogService = inject(ConfirmDialogService);
  private readonly ordersConfirmDialogService = inject(OrdersConfirmDialogService);
  private readonly ordersOperationsService = inject(OrdersOperationsService);
  private readonly alertService = inject(AlertService);
  private readonly translateService = inject(TranslateService);
  private readonly cdr = inject(ChangeDetectorRef);

  private readonly payloadAndOrderItem = this.store.selectSignal(
    fromOrders.selectors.selectPayloadAndOrderItem,
  );
  private readonly selectedOrderItem = this.store.selectSignal(fromOrders.selectors.getSelectedOrderItem);

  private readonly payloadAndOrderItemEffect = effect(() => {
    const data = this.payloadAndOrderItem();
    const payload = data?.payload;
    const orderItem = data?.orderItem;

    if (!payload) {
      this.selectedPaymentMode = null;
      this.actionLevel = null;
      this.dialog?.close();
    } else if (payload?.orderActionType === OrdersActionType.Cancel) {
      this.salesService
        .getPaymentMode()
        .pipe(take(1))
        .subscribe((paymentModes) => {
          this.actionLevel = payload?.level;
          this.cancelDisplayType = payload?.displayType;

          switch (this.cancelDisplayType) {
            case OrderDisplayType.SingleSeatSelection:
            case OrderDisplayType.MultipleSeatSelection:
              this.orderId = orderItem?.orderId;
              this.cancelseats = payload?.items;
              this.onInitSeats(orderItem?.orderId, payload, paymentModes);
              break;
            case OrderDisplayType.MultipleSubscriptionSelection:
              this.initSubscriptionCancelOrRefund(payload, paymentModes);
              break;
            case OrderDisplayType.SubscriptionSeatsSelection:
              this.initSubscriptionSeatsCancelOrRefund(payload);
              break;
            case OrderDisplayType.MultipleFolderSelection:
            case OrderDisplayType.OrderSelection:
            case OrderDisplayType.SingleFolderSelection:
              this.cancelFolders = payload?.items;
              this.onInitFolders(
                payload?.items?.[0]?.orderId,
                payload,
                paymentModes,
                payload?.items?.[0]?.id,
              );
              break;
          }
          this.validationBtnLabel = this.getValidationButtonLabel(this.cancelDisplayType);
        });
    }
  });

  public selectedCustomer: CustomerDisplayItem;
  public cancelFolders: GetOrderItemDetailsResponseDto[];
  public cancelseats: OrderArticleSeatResponseDto[];
  public seatsNumber: number;
  public orderId: number;
  public paymentTypes = [PaymentTypeEnumRequestDto.Direct];
  public refundAmount: number;
  public selectedPaymentMode: PaymentModeResponseDto;
  public cancelPaymentModes: SalesPaymentModeModel[] = [];
  public isPaymentStepInProgress: boolean;
  public isCustomerStepInProgress: boolean;
  private refundsPayload: FinancialTransactionRequestDto[];
  public actionLevel: OrdersActionLevel;
  public cancelDisplayType: OrderDisplayType;
  public orderDisplayType = OrderDisplayType;
  public customerTypeEnum = CustomerType;
  public amountToReturn: number;
  public validationBtnLabel: string;
  public isPaymentEligible: boolean;

  // --- Formule (volet), GEN-6319 : annulation (réservé) / remboursement (payé/édité) souscription-aware.
  // `subscriptionAction` distingue les deux parcours ; les séances éligibles + montants réels sont
  // résolus via getSubscriptionOrderItems (les dossiers du volet portent 0 € par séance sur un abonnement).
  private subscriptionAction:
    | typeof SUBSCRIPTION_CANCEL_ACTION
    | typeof SUBSCRIPTION_REFUND_ACTION
    | null = null;
  private subscriptionActionOrderId: number | null = null;
  public subscriptionItems: OrderItemRowSubscriptionResponseDto[] = [];
  public subscriptionTotalAmount = 0;
  // Dossiers-évènements ciblés par l'action (vide = formule entière via le bouton racine ; non vide =
  // sous-ensemble via l'action « Annuler » par ligne). Sert à contextualiser la pop-up : formule vs dossier.
  private subscriptionSelectedFolders: GetOrderItemDetailsResponseDto[] = [];
  // Vrai si l'action ne porte que sur une PARTIE des séances actives de la formule (sous-ensemble) —
  // condition de l'avertissement « 0,00 € » du ticket (« annulation / remboursement partiel ») (GEN-6319).
  private isPartialSubscriptionAction = false;
  // Sièges (`ID_Physical`) visés quand l'action porte sur des places de formule (GEN-7952). Le
  // périmètre réel — une ligne par séance, avec le sous-ensemble d'articles correspondant — est
  // déplié par le serveur : le front ne transporte que la formule et les sièges.
  private subscriptionSeatsPhysicalSeatIds: number[] = [];
  private subscriptionSeatsStatusScope: SeatStatusScopeRequestDto = RESERVED_SEAT_SCOPE;

  public get isSubscriptionRefund(): boolean {
    return (
      (this.cancelDisplayType === OrderDisplayType.MultipleSubscriptionSelection ||
        this.cancelDisplayType === OrderDisplayType.SubscriptionSeatsSelection) &&
      this.subscriptionAction === SUBSCRIPTION_REFUND_ACTION
    );
  }

  // Customer search state
  private readonly customerSearch = inject(CustomerSearchStateService);
  get customerData() {
    return this.customerSearch.customerData;
  }
  get isLoading() {
    return this.customerSearch.isLoading;
  }
  public selectedCustomerId = 0;

  public get isSinglePayment() {
    return this.cancelFolders?.length === 1;
  }

  ngOnInit(): void {
    this.isPaymentStepInProgress = false;
    this.isCustomerStepInProgress = false;
  }

  public onSearchTrigger(event: { searchTerm: string }) {
    this.customerSearch.triggerSearch(event.searchTerm);
  }

  public onCustomerChange(customer: CustomerSearchItem) {
    this.selectedCustomerId = customer.id;
    this.customerService
      .getCustomerDisplayById(customer.id)
      .pipe(take(1))
      .subscribe((displayCustomer) => {
        this.selectedCustomer = displayCustomer;
        this.cdr.markForCheck();
      });
  }

  onInitFolders(
    orderID: number,
    payload: OrdersActionPayload,
    paymentModes: PaymentModeResponseDto[],
    orderItemId: number | null | undefined,
  ) {
    if (this.ordersUtilsService.isReserved(this.cancelFolders)) {
      return this.processReservedCancel(payload, orderID);
    } else {
      this.getCancellableFolders(this.cancelFolders);
      if (this.isSinglePayment && this.cancelFolders?.[0]?.status !== OrderStatusEnumResponseDto.Canceled) {
        this.loadInitialPaymentMode(paymentModes, orderItemId, this.cancelFolders?.[0]?.amount);
        this.setInitialCustomer(this.cancelFolders?.[0]?.customer?.id);
      } else {
        super.ngOnInit();
        this.dialog.open();
      }
    }
  }

  onInitSeats(orderID: number, payload: OrdersActionPayload, paymentModes: PaymentModeResponseDto[]) {
    if (
      this.cancelseats?.length &&
      this.cancelseats.every((seat) => seat?.status === OrderSeatStatusEnumResponseDto.Reserved)
    ) {
      return this.processReservedCancel(payload, orderID);
    } else {
      this.seatsNumber = this.cancelseats?.length;
      this.ordersCancelService
        .getTotalAmount(this.cancelDisplayType, [], this.cancelseats)
        .pipe(take(1))
        .subscribe((amount) => {
          this.loadInitialSeatsinformation(paymentModes, amount);
          this.cdr.markForCheck();
        });
    }
  }

  private loadInitialSeatsinformation(paymentModes: PaymentModeResponseDto[], amount: number) {
    combineLatest([
      this.store.pipe(select(fromOrders.selectors.selectOrderItemPayments)),

      this.store.pipe(select(fromOrders.selectors.getSelectedOrderItem)),
    ])
      .pipe(
        filter(([payments, orderItem]) => {
          const hasPaymentsForCurrentOrderItem = this.hasPaymentsForOrderItem(payments, orderItem?.id);

          if (!hasPaymentsForCurrentOrderItem && amount > 0 && !!orderItem?.id) {
            this.store.dispatch(fromOrders.actions.loadOrderItemPayments({ orderItemId: orderItem.id }));
          }

          return hasPaymentsForCurrentOrderItem || amount === 0;
        }),
        take(1),
      )

      .subscribe(([allOrderPayments, folder]) => {
        this.setInitialCustomer(folder?.customer?.id);
        const currentOrderItemPayments = this.getPaymentsForOrderItem(allOrderPayments, folder?.id);

        if (currentOrderItemPayments.length === 0) {
          this.setFreePaymentMode(paymentModes);
          super.ngOnInit();
          this.dialog.open();
        } else {
          this.loadInitialSeatsPaymentMode(currentOrderItemPayments, paymentModes);
        }
        this.cdr.markForCheck();
      });
  }

  private loadInitialSeatsPaymentMode(
    allOrderPayments: OrderItemPaymentResponseDto[],
    paymentModes: PaymentModeResponseDto[],
  ) {
    const paymentItems = (allOrderPayments || []).filter(
      ({ operationType }) => operationType === PaymentOperationTypeEnumResponseDto.Payment,
    );
    const amount = (this.cancelseats || []).reduce((acc, curr) => acc + (curr?.price?.amount || 0), 0);

    const orderPayment = paymentItems?.[0].items?.[0];
    this.cancelPaymentModes = [];
    this.refundsPayload = [];
    this.refundsPayload.push(this.ordersCancelService.mapOrderPaymentToRefundsPayload(orderPayment, amount));
    this.cancelPaymentModes.push(<SalesPaymentModeModel>{
      amount: Math.round(amount * 100) / 100,
      paymentTypeLabel: orderPayment?.mode.name,
      paymentTypeId: orderPayment.mode.id,
      paymentMode: this.getPaymentMode(orderPayment?.mode?.id, paymentModes),
    });
    super.ngOnInit();
    this.dialog.open();
  }

  private processReservedCancel(payload: OrdersActionPayload, orderID: number) {
    if (this.ordersCancelService.isSeats(payload.displayType)) {
      this.ordersCancelService.processSeatsReservedCancel(
        payload.level,
        this.cancelDisplayType,
        this.cancelseats,
        orderID,
      );
    } else {
      this.ordersCancelService.processOrderReservedCancel(
        payload.level,
        this.cancelDisplayType,
        this.cancelFolders,
      );
    }
  }

  // Annulation / remboursement d'une formule depuis le volet (GEN-6319). On résout les séances
  // éligibles via getSubscriptionOrderItems (mêmes montants réels que la liste — sur un abonnement,
  // les dossiers du volet portent 0 € par séance) AVANT d'agir : annulation = places réservées
  // (confirmation simple, sans mode de paiement) ; remboursement = places payées/éditées (formulaire
  // de mode de paiement). Le total correct dès l'ouverture évite l'auto-sélection du mode « Gratuit ».
  private initSubscriptionCancelOrRefund(
    payload: OrdersActionPayload,
    paymentModes: PaymentModeResponseDto[],
  ): void {
    if (this.dialog?.isDialogOpen()) {
      return;
    }
    this.subscriptionAction =
      payload?.options?.['subscriptionAction'] === SUBSCRIPTION_REFUND_ACTION
        ? SUBSCRIPTION_REFUND_ACTION
        : SUBSCRIPTION_CANCEL_ACTION;
    this.subscriptionActionOrderId = payload?.orderSubscriptions?.[0]?.id ?? null;
    if (this.subscriptionActionOrderId == null || this.subscriptionActionOrderId <= 0) {
      return;
    }
    // `items` non vide = sous-ensemble d'évènements sélectionnés (action par ligne) ; vide = toute la
    // formule (bouton racine). Match via isSameFormulaEvent (id OU nom+date) car l'id d'article peut
    // différer entre le dossier du volet et l'item de souscription (GEN-6319).
    const selectedFolders: GetOrderItemDetailsResponseDto[] = payload?.items ?? [];
    this.subscriptionSelectedFolders = selectedFolders;
    const requestedSubscriptionId = this.subscriptionActionOrderId;
    const eligibleStatuses =
      this.subscriptionAction === SUBSCRIPTION_REFUND_ACTION
        ? [OrderItemStatusEnumResponseDto.Paid, OrderItemStatusEnumResponseDto.Edited]
        : [OrderItemStatusEnumResponseDto.Reserved];
    this.ordersService
      .getSubscriptionOrderItems(this.subscriptionActionOrderId)
      .pipe(
        takeUntil(this.destroy$),
        catchError((error: unknown) => {
          // Échec de résolution des séances : on surface l'erreur (traceId sur 4xx, sinon message
          // générique via l'ErrorInterceptor) et on n'ouvre pas la pop-up (GEN-6319).
          this.alertService.showApiError(error);
          return EMPTY;
        }),
      )
      .subscribe((items) => {
        if (requestedSubscriptionId !== this.subscriptionActionOrderId) {
          return;
        }
        this.subscriptionItems = (items ?? []).filter(
          (item) =>
            eligibleStatuses.includes(item.statusGroup) &&
            (selectedFolders.length === 0 ||
              selectedFolders.some((folder) => isSameFormulaEvent(item, folder))),
        );
        if (!this.subscriptionItems.length) {
          this.alertService.showCustom('orders.info.noEligibleFormulaSeats', 'warning');
          return;
        }
        // « Partiel » = on agit sur strictement moins de séances que toutes les séances actives de la
        // formule (hors annulées). Sert à borner l'avertissement 0 € au cas partiel (GEN-6319).
        const allActiveSeances = (items ?? []).filter(
          (item) => item.statusGroup !== OrderItemStatusEnumResponseDto.Canceled,
        );
        this.isPartialSubscriptionAction = this.subscriptionItems.length < allActiveSeances.length;
        this.subscriptionTotalAmount = this.moneyService.calculateTotalAmount(
          this.subscriptionItems,
          (item) => item?.amount || 0,
        );
        this.seatsNumber = this.subscriptionItems.reduce((acc, item) => acc + (item?.quantity || 0), 0);
        if (this.subscriptionAction === SUBSCRIPTION_REFUND_ACTION) {
          this.openSubscriptionRefund();
        } else {
          this.confirmSubscriptionCancel();
        }
        this.cdr.markForCheck();
      });
  }

  private openSubscriptionRefund(): void {
    this.refundAmount = Math.round(this.subscriptionTotalAmount * 100) / 100;
    this.amountToReturn = 0;
    this.cancelPaymentModes = [];
    this.refundsPayload = [];
    this.setInitialCustomer(this.selectedOrderItem()?.customer?.id);
    // Construit le formulaire (rows géré par app-payment-mode-form) + câble paymentModes$/totalAmount$.
    super.ngOnInit();
    this.dialog.open();
  }

  private confirmSubscriptionCancel(): void {
    // `actionLevel` peut être remis à null par closeAndDisableEditMode() avant que l'utilisateur valide
    // l'avertissement 0 € différé → on capture le niveau maintenant pour le dispatch (revue Mariem, GEN-6319).
    const level = this.actionLevel;
    const proceed = () => this.dispatchSubscriptionCancel(level);
    const reset = () => this.store.dispatch(fromOrders.actions.resetOrderAction());
    // Deux scénarios distincts (GEN-6319) :
    // - Formule entière (bouton racine « Annuler la réservation X/Y », aucun dossier sélectionné) →
    //   pop-up contextualisée formule (« Annulation de la formule N°… ») ; + avertissement 0 € si
    //   annulation partielle d'un abonnement à 0,00 €.
    // - Dossier / sous-ensemble (action « Annuler » par ligne) → pop-up d'annulation de dossier existante
    //   (dossier unique = « Annulation du dossier N°{id} », sinon générique « annuler la sélection »).
    // Avertissement 0 € : toute annulation PARTIELLE d'un abonnement à 0,00 € (racine OU par-ligne), car
    // annuler une partie d'une formule à 0 € en fait un cas « partiel » au sens du ticket (GEN-6319).
    if (this.subscriptionTotalAmount === 0 && this.isPartialSubscriptionAction) {
      this.ordersConfirmDialogService.showZeroAmountFormulaWarning('cancel', proceed, reset);
      return;
    }

    const isWholeFormula = this.subscriptionSelectedFolders.length === 0;

    if (isWholeFormula) {
      this.confirmDialogService.confirm({
        iconName: 'type=danger',
        title: this.translateService.instant('orders.CancellationOfFormula', {
          folder: this.subscriptionActionOrderId,
        }),
        subtitle: this.translateService.instant('orders.info.placesOfSelectionWillBeReleased'),
        cancelBtnLabel: this.translateService.instant('orders.info.backToSelection'),
        validateBtnLabel: this.translateService.instant('orders.info.yesConfirmAnnulationSelection'),
        validate: proceed,
        cancel: reset,
      });
      return;
    }

    const isSingleFolder = this.subscriptionSelectedFolders.length === 1;
    this.confirmDialogService.confirm({
      iconName: 'type=danger',
      title: isSingleFolder
        ? `${this.translateService.instant('orders.CancellationOfFolderNumber')} ${this.subscriptionSelectedFolders[0]?.id}`
        : this.translateService.instant('orders.info.areYouSureWantCancelSelection'),
      subtitle: this.translateService.instant(
        isSingleFolder
          ? 'orders.info.placesOfFolderWillBeReleased'
          : 'orders.info.placesOfSelectionWillBeReleased',
      ),
      cancelBtnLabel: this.translateService.instant('orders.info.backToSelection'),
      validateBtnLabel: this.translateService.instant(
        isSingleFolder ? 'orders.info.yesConfirmAnnulationFolder' : 'orders.info.yesConfirmAnnulationSelection',
      ),
      validate: proceed,
      cancel: reset,
    });
  }

  // Regroupe les séances résolues par orderId (une formule peut relever de plusieurs commandes) pour
  // ne pas rattacher des items au mauvais order — même logique que PaymentTransactionService.buildOrdersMap.
  private buildSubscriptionOrderItemsMap(): Map<number, number[]> {
    const orderItemsByOrderId = new Map<number, number[]>();
    for (const item of this.subscriptionItems) {
      const itemIds = orderItemsByOrderId.get(item.orderId) ?? [];
      itemIds.push(item.id);
      orderItemsByOrderId.set(item.orderId, itemIds);
    }
    return orderItemsByOrderId;
  }

  private dispatchSubscriptionCancel(level: OrdersActionLevel): void {
    const orders: CancelReservedOrderDto[] = Array.from(this.buildSubscriptionOrderItemsMap().entries()).map(
      ([orderId, orderItemIds]) => ({
        orderId,
        orderDetails: { orderItemIds, orderSubscriptionIds: [] } as CancelReservedOrderDetailsDto,
      }),
    );
    const request: CancelReservedOrderItemsAndSubscriptionsRequestDto = { orders };
    const firstItem = this.subscriptionItems[0];
    this.store.dispatch(
      fromOrders.actions.processSubscriptionCancel({
        request,
        orderId: firstItem?.orderId,
        orderItemId: firstItem?.id,
        level,
      }),
    );
  }

  private dispatchSubscriptionRefund(level: OrdersActionLevel): void {
    const payerId = this.selectedCustomer?.id ?? -1;
    const orderFinancialTransactionItems: OrderFinancialTransactionRequestDto[] =
      this.paymentTransactionService.buildRefundPayloadFromForm(this.form, payerId, this.amountToReturn);
    const orders: OrderRefundDto[] = Array.from(this.buildSubscriptionOrderItemsMap().entries()).map(
      ([orderId, orderItemIds]) => ({
        orderId,
        orderDetails: { orderItemIds, orderSubscriptionIds: [] } as OrderRefundDetailsDto,
      }),
    );
    const request: OrdersRefundRequestDto = { orders, orderFinancialTransactionItems };
    const firstItem = this.subscriptionItems[0];
    this.store.dispatch(
      fromOrders.actions.processSubscriptionRefund({
        request,
        orderId: firstItem?.orderId,
        orderItemId: firstItem?.id,
        level,
      }),
    );
  }

  /**
   * Annulation / remboursement de places d'une formule, sur TOUTES ses séances (GEN-7952).
   *
   * Le front ne résout rien : il transporte la formule (`ID_PackageRow`) et les sièges
   * (`ID_Physical`), et le serveur déplie le périmètre. Les montants suivent la même règle — sur un
   * abonnement le prix est porté par la formule, donc seule la commande sait ce qu'elle doit rendre :
   * on l'obtient du `preflight` au lieu de le recalculer à partir des places affichées.
   */
  private initSubscriptionSeatsCancelOrRefund(payload: OrdersActionPayload): void {
    if (this.dialog?.isDialogOpen()) {
      return;
    }
    this.subscriptionAction =
      payload?.options?.['subscriptionAction'] === SUBSCRIPTION_REFUND_ACTION
        ? SUBSCRIPTION_REFUND_ACTION
        : SUBSCRIPTION_CANCEL_ACTION;
    this.subscriptionActionOrderId = payload?.orderSubscriptions?.[0]?.id ?? null;
    this.subscriptionSeatsPhysicalSeatIds = payload?.options?.['physicalSeatIds'] ?? [];
    // Le perimetre de statut est decide la ou l'action est choisie (la ligne de la place cliquee) :
    // « liberer » ne touche que du reserve, « rembourser » que du regle.
    this.subscriptionSeatsStatusScope =
      this.subscriptionAction === SUBSCRIPTION_REFUND_ACTION ? SETTLED_SEAT_SCOPE : RESERVED_SEAT_SCOPE;
    this.cancelseats = payload?.items ?? [];
    this.seatsNumber = this.cancelseats.length;
    this.orderId = this.selectedOrderItem()?.orderId;

    if (
      this.subscriptionActionOrderId == null ||
      this.subscriptionActionOrderId <= 0 ||
      !this.subscriptionSeatsPhysicalSeatIds.length
    ) {
      // Défensif : `buildSubscriptionSeatsCancelPayload` ne produit ce displayType qu'avec une
      // souscription et des sièges exploitables. Si l'invariant tombe un jour, on ferme l'action au
      // lieu de la laisser en cours — sans quoi le volet resterait bloqué sur une action fantôme.
      this.store.dispatch(fromOrders.actions.resetOrderAction());
      return;
    }

    if (this.subscriptionAction === SUBSCRIPTION_CANCEL_ACTION) {
      this.confirmSubscriptionSeatsCancel();
      return;
    }

    const requestedSubscriptionId = this.subscriptionActionOrderId;
    this.ordersOperationsService
      .cancelPreflight(
        buildSubscriptionSeatsCancelRequest(
          this.subscriptionActionOrderId,
          this.subscriptionSeatsPhysicalSeatIds,
          this.subscriptionSeatsStatusScope,
        ),
      )
      .pipe(
        takeUntil(this.destroy$),
        catchError((error: unknown) => {
          // Échec du plan : on surface l'erreur (traceId sur 4xx) et on n'ouvre pas la pop-up —
          // ouvrir un formulaire de remboursement sans montant fiable inviterait à valider un
          // montant faux.
          this.alertService.showApiError(error);
          return EMPTY;
        }),
      )
      .subscribe((plan) => {
        if (requestedSubscriptionId !== this.subscriptionActionOrderId) {
          return;
        }
        this.subscriptionTotalAmount = plan?.totalDue ?? 0;
        this.openSubscriptionRefund();
        this.cdr.markForCheck();
      });
  }

  /**
   * Libération de places réservées : confirmation simple, sans mode de paiement (une réservation ne
   * rend pas d'argent). Le sous-titre dit explicitement que la place part sur TOUS les évènements de
   * la formule — c'est la seule différence visible avec l'annulation d'une place de séance sèche, et
   * c'est justement ce que l'opérateur doit savoir avant de valider.
   */
  private confirmSubscriptionSeatsCancel(): void {
    const level = this.actionLevel;
    const isSingleSeat = this.cancelseats?.length === 1;

    this.confirmDialogService.confirm({
      iconName: 'type=danger',
      title: this.translateService.instant(
        isSingleSeat ? 'orders.info.areYouSureWantCancelPlace' : 'orders.info.areYouSureWantCancelSelection',
      ),
      subtitle: this.translateService.instant(
        isSingleSeat
          ? 'orders.info.seatOfFormulaWillBeReleasedOnAllEvents'
          : 'orders.info.seatsOfFormulaWillBeReleasedOnAllEvents',
      ),
      cancelBtnLabel: this.translateService.instant('orders.info.backToSelection'),
      validateBtnLabel: this.translateService.instant(
        isSingleSeat ? 'orders.info.yesConfirmAnnulationSeat' : 'orders.info.yesConfirmAnnulationSelection',
      ),
      validate: () => this.dispatchSubscriptionSeatsCancel(level),
      cancel: () => this.store.dispatch(fromOrders.actions.resetOrderAction()),
    });
  }

  /**
   * Instruction de paiement du remboursement. Un seul mode part en `SingleMode` SANS montant : le
   * montant dû est celui que la commande recalcule, donc le lui laisser évite qu'un arrondi du
   * formulaire ne fasse échouer la porte monétaire. Plusieurs modes partent en `Split`, où la
   * répartition est justement l'information que le serveur n'a pas.
   */
  private buildRefundInstruction(): PaymentInstructionRequestDto | undefined {
    // Rien à rendre : le contrat REFUSE alors toute instruction (422
    // `order-operation.unexpected-payment-mode`, « No payment mode may be supplied when nothing is
    // due »). Le formulaire ne peut pas servir de signal : il auto-sélectionne GRATUIT dès que le
    // total vaut 0, ce qui arrive sur un abonnement dont les séances sont à 0 €. C'est donc le
    // montant dû — celui du preflight — qui décide.
    if (!(this.subscriptionTotalAmount > 0)) {
      return undefined;
    }

    const rows = (this.getEffectiveRefundRows() ?? []).filter((row) => row?.mode?.id > 0);
    if (!rows.length) {
      return undefined;
    }
    if (rows.length === 1) {
      return {
        kind: SINGLE_MODE_INSTRUCTION_KIND,
        paymentModeId: rows[0].mode.id,
      } as SingleModePaymentRequestDto;
    }

    return {
      kind: SPLIT_INSTRUCTION_KIND,
      slices: rows.map((row) => ({ paymentModeId: row.mode.id, amount: row.amount ?? 0 })),
    } as SplitPaymentRequestDto;
  }

  private dispatchSubscriptionSeatsCancel(
    level: OrdersActionLevel,
    instruction?: PaymentInstructionRequestDto,
  ): void {
    const request = buildSubscriptionSeatsCancelRequest(
      this.subscriptionActionOrderId,
      this.subscriptionSeatsPhysicalSeatIds,
      this.subscriptionSeatsStatusScope,
      instruction,
    );

    this.store.dispatch(
      fromOrders.actions.processSubscriptionSeatsCancel({
        request,
        orderId: this.orderId ?? this.selectedOrderItem()?.orderId,
        orderItemId: this.cancelseats?.[0]?.orderItemId ?? this.selectedOrderItem()?.id,
        level,
        action: this.subscriptionAction === SUBSCRIPTION_REFUND_ACTION ? 'refund' : 'cancel',
      }),
    );
  }

  private processSubscriptionRefund(): void {
    // Idem cancel : capture du niveau avant l'avertissement 0 € différé (closeAndDisableEditMode nullifie
    // actionLevel juste après cet appel) (revue Mariem, GEN-6319).
    const level = this.actionLevel;
    const proceed = () => this.dispatchSubscriptionRefund(level);
    // Remboursement PARTIEL d'une formule à 0,00 € (abonnement) : avertissement non bloquant avant le
    // remboursement (GEN-6319, ticket « annulation / remboursement partiel »).
    if (this.subscriptionTotalAmount === 0 && this.isPartialSubscriptionAction) {
      this.ordersConfirmDialogService.showZeroAmountFormulaWarning('refund', proceed, () => {});
      return;
    }
    proceed();
  }

  getPaymentModes() {
    return this.salesService.getPaymentMode();
  }

  getTotalAmount() {
    if (
      this.cancelDisplayType === OrderDisplayType.MultipleSubscriptionSelection ||
      this.cancelDisplayType === OrderDisplayType.SubscriptionSeatsSelection
    ) {
      return of(this.subscriptionTotalAmount);
    }
    return this.ordersCancelService.getTotalAmount(
      this.cancelDisplayType,
      this.cancelFolders,
      this.cancelseats,
    );
  }

  public createForm(): void {
    const initialPaymentRows = (this.cancelPaymentModes || []).map((mode) => {
      return this.formBuilder.group({
        mode: [mode?.paymentMode, Validators.required],
        amount: [mode?.amount, [Validators.required, Validators.min(0)]],
      });
    });
    this.form = this.formBuilder.group(
      {
        rows: this.formBuilder.array(initialPaymentRows),
      },
      {
        validators: [this.hasUnfilledAmountValidator()],
        asyncValidators: [this.paymentModeAmountsSumValidator(this.totalAmount$)],
      },
    );
  }

  public open() {
    this.dialog.open({ disableClose: true });
  }

  public closeAndDisableEditMode() {
    if (
      this.actionLevel === OrdersActionLevel.List &&
      this.cancelDisplayType !== OrderDisplayType.MultipleSeatSelection &&
      this.cancelDisplayType !== OrderDisplayType.SingleSeatSelection
    ) {
      this.store.dispatch(fromOrders.actions.resetOrderDetails());
    }
    this.isPaymentStepInProgress = false;
    this.isCustomerStepInProgress = false;
    this.actionLevel = null;
  }

  public closeAndLeaveCancel() {
    this.closeAndDisableEditMode();
    this.store.dispatch(fromOrders.actions.resetOrderAction());
  }

  private mapToPaymentItem(item: PaymentModeModel) {
    return <FinancialTransactionRequestDto>{
      id: -1,
      paymentId: -1,
      paymentModeId: item?.mode.id,
      label: item?.mode.name,
      amount: item?.amount || 0,
      paymentOperationType: PaymentOperationTypeEnumRequestDto.Refund,
    };
  }

  /**
   * Lignes de mode de paiement effectivement retenues pour le remboursement. Un mode gratuit se
   * rembourse tel quel ; sinon c'est le formulaire qui décide de la répartition
   * (`getDirectPaymentResult`). Extrait pour que le contrat `orders/operations`, qui attend une
   * instruction de paiement et non des transactions financières, parte des MEMES lignes.
   */
  private getEffectiveRefundRows(): PaymentModeModel[] | undefined {
    const paymentItems = clone(this.form?.controls?.rows.value);
    const isFreeMode = paymentItems?.every(
      ({ mode }) => ~Constants.FreeMode.indexOf(mode?.paymentType?.code),
    );

    // `undefined` et non `[]` quand la répartition ne rend rien : `setRefundPayload` le propage aux
    // chemins historiques, qui OMETTENT alors `refunds` du corps de requête. Un tableau vide y
    // serait un changement de contrat, invisible ici et hors sujet.
    return isFreeMode
      ? paymentItems
      : AbstractPaymentModeFormUtils.getDirectPaymentResult(paymentItems, this.amountToReturn);
  }

  private setRefundPayload(): FinancialTransactionRequestDto[] {
    return this.getEffectiveRefundRows()?.map((data: PaymentModeModel) => this.mapToPaymentItem(data));
  }

  public processOrderCancel() {
    // Seul le remboursement de formule ouvre ce dialog (l'annulation réservée passe par une simple
    // confirmation, sans mode de paiement) — d'où le branchement direct vers le remboursement (GEN-6319).
    if (this.cancelDisplayType === OrderDisplayType.MultipleSubscriptionSelection) {
      this.processSubscriptionRefund();
      this.closeAndDisableEditMode();
      return;
    }
    if (this.cancelDisplayType === OrderDisplayType.SubscriptionSeatsSelection) {
      this.dispatchSubscriptionSeatsCancel(this.actionLevel, this.buildRefundInstruction());
      this.closeAndDisableEditMode();
      return;
    }
    if (this.ordersCancelService.isSeats(this.cancelDisplayType)) {
      this.processSeatsCancel(this.orderId);
    } else {
      this.processOrderItemsCancel();
    }
    this.closeAndDisableEditMode();
  }

  private hasFreePaymentMode() {
    const hasFreeMode = this.form?.controls?.rows?.value.filter(
      (paymentModeitem) => paymentModeitem?.mode?.paymentType?.code == Constants.FreeMode,
    );
    return hasFreeMode.length > 0;
  }

  private processSeatsCancel(orderId: number) {
    if (!this.hasFreePaymentMode()) {
      this.refundsPayload = this.setRefundPayload();
    }

    this.setRefundedCustomer(this.selectedCustomer);

    const orderSeatsCancel: OrderSeatsCancelRequestDto = {
      refunds: this.refundsPayload,
      seatIds:
        this.cancelDisplayType === OrderDisplayType.SingleSeatSelection
          ? [this.cancelseats[0]?.id]
          : this.cancelseats?.map((seat) => seat?.id),
    };

    this.store.dispatch(
      fromOrders.actions.processSeatsCancel({
        orderArticles: {
          orderID: orderId,
          orderItemId: this.cancelseats?.[0]?.orderItemId,
          orderArticleItems: this.cancelseats,
        },
        orderSeatsCancel: orderSeatsCancel,
        level: this.actionLevel,
      }),
    );
  }

  private processOrderItemsCancel() {
    if (this.isPaymentStepInProgress) {
      this.refundsPayload = this.setRefundPayload();
    }

    if (this.cancelDisplayType !== OrderDisplayType.MultipleFolderSelection) {
      this.setRefundedCustomer(this.selectedCustomer);
    }

    const orderItemCancel: CancelOrderItemsRequest = {
      refunds: this.refundsPayload,
      orderId:
        this.cancelDisplayType === OrderDisplayType.OrderSelection ? this.cancelFolders?.[0]?.orderId : null,
      orderItemIds: this.isSinglePayment
        ? [this.cancelFolders[0]?.id]
        : this.cancelFolders?.map((folder) => folder?.id),
    };

    this.store.dispatch(
      fromOrders.actions.processOrderCancel({
        orderItemCancel: orderItemCancel,
        orderId: !!orderItemCancel?.orderId ? orderItemCancel?.orderId : this.cancelFolders?.[0]?.orderId,
        level: this.actionLevel,
      }),
    );
  }
  private setInitialCustomer(consumerId: number) {
    this.customerService.getCustomerDisplayById(consumerId).subscribe({
      next: (customer: CustomerDisplayItem) => {
        this.selectedCustomer = customer;
        this.selectedCustomerId = customer.id;
        this.customerSearch.addToListIfMissing(
          this.customerService.mapCustomerDisplayItemToSearchItem(customer),
        );
        this.cdr.markForCheck();
      },
    });
  }

  public getOrderSummaryLabel = (folder: GetOrderItemDetailsResponseDto) => {
    return this.actionLevel === OrdersActionLevel.List
      ? `Dossier N° ${folder?.id}
    (commande N°${folder?.orderId})
    - ${this.getFolderName(folder)}
    ${this.datePipe.transform(this.getFolderDate(folder)) ?? ''}
    - ${this.currencyPipe.transform(folder.amount, 'EUR', 'symbol', '0.2-2', 'fr')}`
      : `${this.getFolderName(folder)}
    ${this.datePipe.transform(this.getFolderDate(folder)) ?? ''}
    - ${this.currencyPipe.transform(folder.amount, 'EUR', 'symbol', '0.2-2', 'fr')}`;
  };

  public getSubscriptionSummaryLabel = (item: OrderItemRowSubscriptionResponseDto) =>
    `${item?.eventName ?? ''}
    ${this.datePipe.transform(item?.eventDate) ?? ''}
  - ${this.currencyPipe.transform(item?.amount, 'EUR', 'symbol', '0.2-2', 'fr')}`;

  public getseatSummaryLabel = (seat: OrderArticleSeatResponseDto | OrderArticleProductResponseDto) => {
    const isProduct = !(seat as OrderArticleSeatResponseDto)?.seatInfos;
    const currentSeat = seat as OrderArticleSeatResponseDto;
    const currentProduct = seat as OrderArticleProductResponseDto;
    const seatSummary = isProduct
      ? ` ${currentProduct?.product?.designation}
    - ${this.currencyPipe.transform(currentProduct?.price?.amount, 'EUR', 'symbol', '0.2-2', 'fr')}`
      : ` ${currentSeat?.category?.name}
    - ${currentSeat?.seatInfos?.accessCode} ${currentSeat?.seatInfos?.rank} ${
          currentSeat?.seatInfos?.seatNumber
        }
    - ${seat?.price?.name}
    - ${this.currencyPipe.transform(currentSeat?.price?.amount, 'EUR', 'symbol', '0.2-2', 'fr')}`;
    return currentSeat?.barCode ? ` ${seatSummary} - ${currentSeat?.barCode}` : seatSummary;
  };

  public getFolderName(folder: GetOrderItemDetailsResponseDto) {
    return folder?.type === OrderItemTypeEnumResponseDto.Product
      ? folder?.product?.designation
      : folder?.session?.name;
  }

  public getFolderDate(folder: GetOrderItemDetailsResponseDto) {
    return folder?.session?.startDate;
  }

  private getCancellableFolders(folders: GetOrderItemDetailsResponseDto[]) {
    const refundFolders = this.ordersCancelService.getCancellableFolders(folders);
    const refundAmount = (refundFolders || []).reduce((acc, curr) => {
      return acc + curr?.amount;
    }, 0);
    this.refundAmount = Math.round(refundAmount * 100) / 100;
    this.seatsNumber = (refundFolders || []).reduce((acc, curr) => {
      return acc + curr?.quantity;
    }, 0);
    this.cancelFolders = refundFolders;
  }

  editPaymentMode() {
    this.isPaymentStepInProgress = true;
  }

  private getPaymentMode(paymentModeId: number, paymentModeitems: PaymentModeResponseDto[]) {
    return (paymentModeitems || []).find(({ id }) => id === paymentModeId);
  }

  private getPaymentModesWithoutPayback(
    paybackAmount: number,
    orderPayments: OrderItemPaymentItemResponseDto[],
    paymentModeitems: PaymentModeResponseDto[],
  ) {
    this.refundsPayload = [];
    const paymentModes = orderPayments.map((paymentMode) => {
      let amount = 0;
      [amount, paybackAmount] = this.ordersCancelService.CalculateCurrentAmount(paymentMode, paybackAmount);
      if (amount > 0) {
        this.refundsPayload.push(
          this.ordersCancelService.mapOrderPaymentToRefundsPayload(paymentMode, amount),
        );
      }
      return this.ordersCancelService.mapOrderPaymentToPaymentMode(paymentMode, paymentModeitems, amount);
    });
    this.cancelPaymentModes = (paymentModes || []).filter(({ amount }) => amount > 0);
    this.refundsPayload = (this.refundsPayload || []).filter(({ amount }) => amount > 0);
    if (this.cancelPaymentModes.length === 0) {
      this.setFreePaymentMode(paymentModeitems);
    }
  }

  private setFreePaymentMode(paymentModeitems: PaymentModeResponseDto[]) {
    const freeMode = paymentModeitems.find((mode) => ~Constants.FreeMode.indexOf(mode?.paymentType?.code));
    this.refundsPayload = [this.ordersCancelService.setRefundsPayload(freeMode, 0)];
    this.cancelPaymentModes = [];
    this.cancelPaymentModes.push(<SalesPaymentModeModel>{
      amount: 0,
      paymentTypeId: freeMode.paymentType?.id,
      paymentTypeLabel: freeMode?.name,
      paymentMode: freeMode,
      paymentOperationType: PaymentOperationTypeEnumRequestDto.Refund,
    });
  }

  private loadInitialPaymentMode(
    paymentModes: PaymentModeResponseDto[],
    orderItemId: number | null | undefined,
    amount: number,
  ) {
    this.store
      .pipe(
        select(fromOrders.selectors.selectOrderItemPayments),
        filter((payments) => {
          const hasPaymentsForCurrentOrderItem = this.hasPaymentsForOrderItem(payments, orderItemId);

          if (!hasPaymentsForCurrentOrderItem && amount > 0 && !!orderItemId) {
            this.store.dispatch(fromOrders.actions.loadOrderItemPayments({ orderItemId }));
          }

          return hasPaymentsForCurrentOrderItem || amount === 0;
        }),
        take(1),
      )
      .subscribe({
        next: (allOrderPayments) => {
          const currentOrderItemPayments = this.getPaymentsForOrderItem(allOrderPayments, orderItemId);

          if (currentOrderItemPayments.length === 0) {
            this.setFreePaymentMode(paymentModes);
          } else {
            //load, filter and calculate payment mode if has payback
            const paymentItems = (currentOrderItemPayments || []).filter(
              ({ operationType }) => operationType === PaymentOperationTypeEnumResponseDto.Payment,
            );
            const paybackAmount = this.ordersCancelService.getPaybackAmount(currentOrderItemPayments);
            const orderPayments = paymentItems
              .map(({ items }) => items)
              .reduce((acc, curr) => [...acc, ...curr], []);

            this.getPaymentModesWithoutPayback(paybackAmount, orderPayments, paymentModes);
          }
          super.ngOnInit();
          this.dialog.open();
          this.cdr.markForCheck();
        },
      });
  }

  private hasPaymentsForOrderItem(
    orderItemPayments: OrderItemPaymentResponseDto[] | null | undefined,
    orderItemId: number | null | undefined,
  ) {
    if (!orderItemId) {
      return false;
    }

    return (orderItemPayments || []).some((payment) => payment?.orderItemId === orderItemId);
  }

  private getPaymentsForOrderItem(
    orderItemPayments: OrderItemPaymentResponseDto[] | null | undefined,
    orderItemId: number | null | undefined,
  ): OrderItemPaymentResponseDto[] {
    if (!orderItemId) {
      return [];
    }

    return (orderItemPayments || []).filter((payment) => payment?.orderItemId === orderItemId);
  }

  public paymentModeChange(paymentMode: PaymentModeResponseDto) {
    this.selectedPaymentMode = paymentMode;
    this.refundsPayload = [this.ordersCancelService.setRefundsPayload(paymentMode, this.refundAmount)];
  }

  private setRefundedCustomer(payer: CustomerDisplayItem | null) {
    this.refundsPayload.map((refund) => {
      refund.payerId = !payer ? -1 : payer?.id;
    });
  }

  public disabledCancelButton() {
    if (
      this.cancelDisplayType === OrderDisplayType.MultipleSubscriptionSelection ||
      this.cancelDisplayType === OrderDisplayType.SubscriptionSeatsSelection
    ) {
      return !this.selectedCustomer || !this.form?.valid;
    }
    if (this.ordersCancelService.isSeats(this.cancelDisplayType)) {
      return !this.selectedCustomer || !this.form?.valid;
    }
    return (
      (!this.isSinglePayment && !this.selectedPaymentMode) ||
      (this.isCustomerStepInProgress && !this.selectedCustomer) ||
      !this.form?.valid
    );
  }

  public isProduct(cancelseats: OrderArticleSeatResponseDto[] | OrderArticleProductResponseDto[]) {
    return !(cancelseats?.[0] as OrderArticleSeatResponseDto)?.seatInfos;
  }

  public showFooterSummaryItem(item: string) {
    return this.ordersCancelService.showFooterSummaryItem(item, this.cancelDisplayType);
  }

  public editCustomer() {
    this.isCustomerStepInProgress = true;
  }

  public getOrdersLength() {
    const ordersIds = this.cancelFolders.map((folder) => folder?.orderId);
    return ordersIds.filter((item, index) => ordersIds.indexOf(item) === index)?.length;
  }

  public getValidationButtonLabel(cancelDisplayType: OrderDisplayType) {
    switch (cancelDisplayType) {
      case OrderDisplayType.MultipleSubscriptionSelection:
      case OrderDisplayType.SubscriptionSeatsSelection:
        return 'orders.validateRefund';

      case OrderDisplayType.SingleFolderSelection:
        return 'orders.cancelAndRefundFolder';

      case OrderDisplayType.OrderSelection:
        return 'orders.cancelAndRefundOrder';

      case OrderDisplayType.MultipleFolderSelection:
        return this.getOrdersLength() === 1
          ? 'orders.cancelAndRefundOrder'
          : 'orders.cancelAndRefundSelection';

      case OrderDisplayType.SingleSeatSelection:
        if (this.isProduct(this.cancelseats)) {
          return 'orders.cancelAndRefundProduct';
        }
        return 'orders.cancelAndRefundSeat';

      case OrderDisplayType.MultipleSeatSelection:
        return 'orders.cancelAndRefundSelection';
    }
  }
}
